import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type AlertEventPayload = {
  id: number;
  title: string;
  text: string;
  start_at?: string;
  end_at?: string | null;
  created_at?: string;
};

type EventPayload =
  | string
  | AlertEventPayload
  | { alert: AlertEventPayload }
  | { id: number; removed?: boolean }
  | null
  | undefined;

export type WebSocketStateItem = string | AlertEventPayload;

const initialState: WebSocketStateItem[] = [];

const getAlertFromPayload = (event: EventPayload): AlertEventPayload | null => {
  if (typeof event !== 'object' || event === null) {
    return null;
  }
  if ('alert' in event && event.alert && typeof event.alert === 'object') {
    const { id, title, text } = event.alert;
    if (typeof id === 'number' && typeof title === 'string' && typeof text === 'string') {
      return event.alert;
    }
  }
  if (
    'id' in event &&
    typeof event.id === 'number' &&
    'title' in event &&
    typeof (event as AlertEventPayload).title === 'string'
  ) {
    return event as AlertEventPayload;
  }
  return null;
};

const isRemoveAlertPayload = (
  event: EventPayload,
): event is { id: number; removed: true } => {
  if (typeof event !== 'object' || event === null) return false;
  return (
    'removed' in event &&
    event.removed === true &&
    typeof (event as { id: number }).id === 'number'
  );
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    addEvent: (state, action: PayloadAction<EventPayload>) => {
      const event = action.payload;

      const alert = getAlertFromPayload(event);
      if (alert) {
        const exists = state.some(
          (item) => typeof item === 'object' && item.id === alert.id,
        );
        if (!exists) {
          state.push(alert);
        }
        return;
      }

      if (isRemoveAlertPayload(event)) {
        const index = state.findIndex(
          (item) => typeof item === 'object' && item.id === event.id,
        );
        if (index !== -1) {
          state.splice(index, 1);
        }
        return;
      }

      if (typeof event === 'string') {
        const normalizedEvent = event.trim();
        if (normalizedEvent.length === 0) return;
        if (!state.includes(normalizedEvent)) {
          state.push(normalizedEvent);
        }
      }
    },
    clearEvents: (state) => {
      state.length = 0;
    },
  },
});

export const { addEvent, clearEvents } = websocketSlice.actions;
export default websocketSlice.reducer;
